import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';

import DataTable from '../shared/DataTable';
import { Caption, TONE, TagChip } from '../shared/designKit';
import { numFont } from '../../utils/fonts';
import { flattenDrfError } from '../../utils/apiUtils';
import ReminderRuleDialog from './ReminderRuleDialog';
import ReminderPreviewDialog from './ReminderPreviewDialog';
import { MobileCard, MobileEmptyState, MobileListContainer } from './MobileCards';
import { channelIcon, channelShortTranslationKey, triggerLabel } from './reminderUtils';

import { can } from '../../utils/access';

const stop = (e) => e.stopPropagation();

export default function RulesTab({ catalog }) {
  const { t, i18n } = useTranslation();
  const orgId = useUserStore((s) => s.currentOrganization?.organization_id);
  const isAdmin = useUserStore((s) => can(s.currentOrganization, 'service_reminders.manage'));

  const [rules, setRules] = useState(null);
  const [dialogRule, setDialogRule] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // preview: { rule, activateAfter } — activateAfter drives the "Aktivoi" button
  const [preview, setPreview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchRules = useCallback(async () => {
    try {
      const response = await axios.get('/api/reminder-rules/', { params: { organization: orgId } });
      setRules(response.data);
    } catch (err) {
      console.error('Failed to load reminder rules:', err);
      setSnackbar({ open: true, message: t('service_reminders.load_error'), severity: 'error' });
      setRules([]);
    }
  }, [orgId, t]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const openDialog = (rule) => {
    setDialogRule(rule);
    setDialogOpen(true);
  };

  const handleSaveRule = async (formData, ruleId) => {
    try {
      if (ruleId) {
        await axios.patch(`/api/reminder-rules/${ruleId}/`, formData, { params: { organization: orgId } });
        setSnackbar({ open: true, message: t('service_reminders.rule_saved'), severity: 'success' });
      } else {
        await axios.post('/api/reminder-rules/', formData, { params: { organization: orgId } });
        setSnackbar({ open: true, message: t('service_reminders.rule_created'), severity: 'success' });
      }
      await fetchRules();
      return true;
    } catch (err) {
      setSnackbar({
        open: true,
        message: flattenDrfError(err, t('service_reminders.save_error')),
        severity: 'error',
      });
      return false;
    }
  };

  const handleToggleActive = async (rule, checked) => {
    if (checked) {
      // Activation goes through the dry-run preview — the workshop sees WHO
      // would get messages before anything can be sent.
      setPreview({ rule, activateAfter: true });
      return;
    }
    try {
      await axios.patch(`/api/reminder-rules/${rule.id}/`, { active: false }, { params: { organization: orgId } });
      setSnackbar({ open: true, message: t('service_reminders.rule_deactivated'), severity: 'success' });
      await fetchRules();
    } catch (err) {
      setSnackbar({ open: true, message: flattenDrfError(err, t('service_reminders.save_error')), severity: 'error' });
    }
  };

  const handleActivate = async (rule) => {
    try {
      await axios.patch(`/api/reminder-rules/${rule.id}/`, { active: true }, { params: { organization: orgId } });
      setSnackbar({ open: true, message: t('service_reminders.rule_activated'), severity: 'success' });
      setPreview(null);
      await fetchRules();
    } catch (err) {
      setSnackbar({ open: true, message: flattenDrfError(err, t('service_reminders.save_error')), severity: 'error' });
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/reminder-rules/${deleteTarget.id}/`, { params: { organization: orgId } });
      setSnackbar({ open: true, message: t('service_reminders.rule_deleted'), severity: 'success' });
      setDeleteTarget(null);
      await fetchRules();
    } catch (err) {
      setSnackbar({ open: true, message: flattenDrfError(err, t('service_reminders.save_error')), severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (rules === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // The default name already says the trigger ("Öljynvaihto 12 kk"): then one line is enough.
  const triggerLine = (rule) => {
    const label = triggerLabel(catalog, rule.trigger_key, i18n.language);
    return rule.name.toLowerCase().includes(label.toLowerCase()) ? null : label;
  };

  const activeControl = (rule, testId) => (
    <Box onClick={stop} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {isAdmin ? (
        <Switch
          size="small"
          checked={rule.active}
          onChange={(e) => handleToggleActive(rule, e.target.checked)}
          inputProps={testId ? { 'data-testid': testId } : undefined}
        />
      ) : (
        <TagChip
          label={rule.active ? t('service_reminders.active') : t('service_reminders.inactive')}
          tone={rule.active ? TONE.green : TONE.slate}
        />
      )}
    </Box>
  );

  // Preview first, edit, then delete: the E2E spec clicks the last button of a row to delete.
  const rowActions = (rule, { testIds = true } = {}) => (
    <Box sx={{ display: 'flex', whiteSpace: 'nowrap', gap: 0.25 }} onClick={stop}>
      <Tooltip title={t('service_reminders.preview_button')}>
        <span>
          <IconButton size="small" onClick={() => setPreview({ rule, activateAfter: false })} sx={{ color: 'text.secondary' }}>
            <GroupsOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {isAdmin && (
        <>
          <Tooltip title={t('service_reminders.edit_rule')}>
            <IconButton
              size="small"
              onClick={() => openDialog(rule)}
              data-testid={testIds ? `rule-edit-${rule.id}` : undefined}
              sx={{ color: 'text.secondary' }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('Delete')}>
            <IconButton size="small" onClick={() => setDeleteTarget(rule)} sx={{ color: 'text.secondary' }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );

  const columns = [
    {
      key: 'name',
      label: t('service_reminders.col_name'),
      maxWidth: 320,
      render: (rule) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: rule.active ? 'text.primary' : 'text.secondary' }}>
            {rule.name}
          </Typography>
          {triggerLine(rule) && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{triggerLine(rule)}</Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'interval',
      label: t('service_reminders.col_interval'),
      width: '1%',
      noWrap: true,
      render: (rule) => (
        <Typography variant="body2" sx={{ fontWeight: 600, ...numFont }}>
          {t('service_reminders.months_value', { months: rule.interval_months })}
        </Typography>
      ),
    },
    {
      key: 'channel',
      label: t('service_reminders.col_channel'),
      noWrap: true,
      render: (rule) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
          {channelIcon(rule.channel, { fontSize: 16 })}
          <Typography variant="body2" sx={{ color: 'text.primary' }}>{t(channelShortTranslationKey(rule.channel))}</Typography>
        </Box>
      ),
    },
    {
      key: 'sent_30d',
      label: t('service_reminders.col_sent_30d'),
      width: '1%',
      align: 'right',
      render: (rule) => (
        <Typography variant="body2" sx={{ ...numFont, color: rule.sent_30d_count ? 'text.primary' : 'text.disabled' }}>
          {rule.sent_30d_count}
        </Typography>
      ),
    },
    {
      key: 'active',
      label: t('service_reminders.col_active'),
      width: '1%',
      render: (rule) => activeControl(rule, `rule-active-switch-${rule.id}`),
    },
    {
      key: 'actions',
      label: '',
      width: '1%',
      render: (rule) => rowActions(rule),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Caption sx={{ flex: '1 1 320px' }}>{t('service_reminders.rules_intro')}</Caption>
        {isAdmin && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => openDialog(null)}
            data-testid="new-rule-button"
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', flexShrink: 0 }}
          >
            {t('service_reminders.new_rule')}
          </Button>
        )}
      </Box>

      {rules.length > 0 && rules.every((rule) => !rule.active) && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
          {t('service_reminders.all_rules_inactive_hint')}
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={rules}
        onRowClick={isAdmin ? (rule) => openDialog(rule) : undefined}
        emptyIcon={<NotificationsNoneIcon />}
        emptyTitle={t('service_reminders.no_rules_title')}
        emptyDescription={t('service_reminders.no_rules_description')}
        testIdPrefix="reminder-rule"
        tableTestId="reminder-rules-table"
      />

      {/* Mobile card list (DataTable hides itself below lg) */}
      <MobileListContainer testId="reminder-rules-mobile">
        {rules.length === 0 ? (
          <MobileEmptyState
            icon={<NotificationsNoneIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
            title={t('service_reminders.no_rules_title')}
            description={t('service_reminders.no_rules_description')}
          />
        ) : (
          rules.map((rule) => (
            <MobileCard
              key={rule.id}
              testId={`reminder-rule-mobile-${rule.id}`}
              onClick={isAdmin ? () => openDialog(rule) : undefined}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }} noWrap>
                    {rule.name}
                  </Typography>
                  {triggerLine(rule) && (
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }} noWrap>
                      {triggerLine(rule)}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flexShrink: 0 }}>{activeControl(rule)}</Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.25, flexWrap: 'wrap', color: 'text.secondary' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', ...numFont }}>
                  {t('service_reminders.months_value', { months: rule.interval_months })}
                </Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {channelIcon(rule.channel, { fontSize: 15 })}
                  <Typography variant="body2">{t(channelShortTranslationKey(rule.channel))}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.75 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', ...numFont }}>
                  {t('service_reminders.col_sent_30d')}: {rule.sent_30d_count}
                </Typography>
                {rowActions(rule, { testIds: false })}
              </Box>
            </MobileCard>
          ))
        )}
      </MobileListContainer>

      <ReminderRuleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveRule}
        rule={dialogRule}
        catalog={catalog}
      />

      {preview && (
        <ReminderPreviewDialog
          open
          onClose={() => setPreview(null)}
          rule={preview.rule}
          catalog={catalog}
          activateAfter={preview.activateAfter && isAdmin}
          onActivate={() => handleActivate(preview.rule)}
        />
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('service_reminders.delete_rule_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('service_reminders.delete_rule_text', { name: deleteTarget?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('Cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
